import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map'

import { ConfirmationService } from 'primeng/primeng';
import { MessageService } from 'primeng/components/common/messageservice';

import { ApiService, getResponse } from '../api.service';
import { Product, SelectItem, ProductImage, ProductShort, Image } from '@models';

import { LazyLoadEvent } from 'primeng/primeng';

@Injectable()
export class ProductService extends ApiService<Product> {

    protected apiGroup: string = 'shop';
	protected apiName: string = 'product';

	protected googleCategoryItems: Array<googleCategoryItem> = [];

	constructor(
		public http: HttpClient,
		public translate: TranslateService,
		public confirmationService: ConfirmationService,
		public messageService: MessageService
	) {
		super(http, translate, confirmationService, messageService);
    }

	/**
	 * getLazyShort(event: LazyLoadEvent)
	 */
	getLazyShort(event: LazyLoadEvent): Observable<getResponse<ProductShort>> {
		let params: {[k: string]: any} = event;
		
        params.fields = ['id', 'active', 'name_nl', 'alias_nl', 'price_incl', 'price_excl'];

		return this.getLazy(event).map(response => {
			return {
				data: <Array<ProductShort>> response.data,
				total: response.total
			};
		});
		
		/*return this.http.post<getResponse<ProductShort>>(
			this.getBaseUrl('get'),
			params
		).retry(this.retries);*/
	}
    
	/**
	 * getImages(parameters)
	 * Retrieve list of all images
     * 
     * @param countryId
	 */
	getImages(countryId: number): Observable<getResponse<Image>> {
        return this.http.get<getResponse<Image>>(
            this.getBaseUrl(countryId.toString(), 'images')
        ).retry(this.retries);
    }
    
    /**
     * uploadImage
     * Upload image to server
     * 
     * @param countryId 
     * @param image 
     */
    uploadImage(countryId: number, image: File): Observable<any> {
        const formData = new FormData();

        formData.append('image', image);

        return this.http.post<any>(
            this.getBaseUrl(countryId.toString(), 'images'),
            formData
        ).retry(this.retries);
    }

    /**
     * deleteImage
     * Delete image from server
     * 
     * @param countryId 
     * @param image_id 
     */
    deleteImage(countryId: number, image_id: number): Observable<any> {
		return  Observable.create(observer => {
			this.confirmationService.confirm({
				message: this.translate.instant("Are you sure?"),
				header: this.translate.instant("Delete " + this.apiName),
				icon: 'fa fa-trash',
                accept: () => {
					this.http.delete<any>(
						this.getBaseUrl(countryId.toString(), ['images', image_id.toString()])
					).subscribe(response => {
						if(response.success) {
							this.messageService.add({severity: 'success', summary: this.translate.instant("Delete " + this.apiName + " Image"), detail: this.translate.instant(this.apiName + " Image successfully deleted")});
						} else {
							let error = (response.error ? response.error : this.translate.instant("Unknown error occured"));
							this.messageService.add({severity: 'danger', summary: this.translate.instant("Delete " + this.apiName + " Image"), detail: error});
						}

						observer.next(response);
						observer.complete();
					}, err => {
						observer.next({
							success: false,
							error: ''
						});
						observer.complete();
					});
				},
				reject: () => {}
			});
		});
    }
	
	moveImage(productId: number, image_id: number, up: boolean): Observable<any> {
        return this.http.post<any>(
            this.getBaseUrl(productId.toString(), ['images', image_id.toString(), 'move']),
            {
            	up: up
            }
        ).retry(0);
	}
    
	/**
	 * getRelatedProducts(parameters)
	 * Retrieve list of all related products
     * 
     * @param productId
	 */
	getRelatedProducts(productId: number): Observable<getResponse<ProductShort>> {
        return this.http.get<getResponse<ProductShort>>(
            this.getBaseUrl('get_related', productId.toString())
        ).retry(this.retries);
    }
    
	/**
	 * addRelatedProduct(parameters)
	 * Add related product to product
     * 
     * @param productId
     * @param relatedProductId
	 */
	addRelatedProduct(productId: number, relatedProductId: number): Observable<{success: boolean}> {
        return this.http.post<any>(
            this.getBaseUrl('add_related', [productId.toString(), relatedProductId.toString()]),
            {}
        ).retry(0);
    }
    
	/**
	 * removeRelatedProduct(parameters)
	 * Remove related product from product
     * 
     * @param productId
     * @param relatedProductId
	 */
	removeRelatedProduct(productId: number, relatedProductId: number): Observable<{success: boolean}> {
        return this.http.post<any>(
            this.getBaseUrl('remove_related', [productId.toString(), relatedProductId.toString()]),
            {}
        ).retry(0);
    }
    
	selectItemArray(): SelectItem[] {
		let array: SelectItem[] = [];

		for(var item of this.items) {
			array.push({
				label: item.name_nl,
				value: item.id
			})
		}

		return array;
	}


	/**
	 * getGoogleCategories()
	 * Retrieve list of all google cateogires
	 */
	getGoogleCategories(): Observable<getResponse<googleCategoryItem>> {
        return this.http.get<getResponse<googleCategoryItem>>(
            this.getBaseUrl('get_google_categories')
        ).retry(this.retries);
    }
    
	loadGoogleCategoryItems(reload: boolean = false): Observable<Array<googleCategoryItem>> {
		if(!reload && this.googleCategoryItems.length > 0) {
			return  Observable.create(observer => {
				observer.next(this.googleCategoryItems);
				observer.complete();
			});
		}
		return this.getGoogleCategories().map(data => {
			this.googleCategoryItems = data.data;

			return this.googleCategoryItems;
		});
    }
    
	selectGoogleCategoryItemArray(): SelectItem[] {
		let array: SelectItem[] = [];

		for(var item of this.googleCategoryItems) {
			array.push({
				label: item.path,
				value: item.id
			})
		}

		return array;
	}
}

interface googleCategoryItem {
	id: string, 
	path: string, 
	name: string
}




// WEBPACK FOOTER //
// /var/www/vhosts/s-wines.nl/httpdocs/crm/resources/assets/src/app/shared/services/shop/product.service.ts